import { makeStyles } from '@material-ui/core/styles';
import { alpha } from '@material-ui/core/styles/colorManipulator';

export const useTreeStyles = makeStyles(theme => ({
  treeContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 400,
    position: 'relative',
  },
  statusBarWrapper: {
    display: 'flex',
    alignItems: 'stretch',
    flexShrink: 0,
  },
  statusBarContent: {
    flex: 1,
    minWidth: 0,
  },
  statusBarAction: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  statusBar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  statusBarSection: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5, 2.5),
    '&:not(:first-child)': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
  },
  statusBarLabel: {
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  statusBarValue: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statusBarDetail: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  treeScrollArea: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
  },
  treeCanvas: {
    position: 'relative',
    minWidth: '100%',
  },
  node: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    overflow: 'visible',
    padding: theme.spacing(0.5, 0),
    '&:hover': {
      borderColor: theme.palette.primary.light,
      boxShadow: theme.shadows[3],
    },
  },
  nodeSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[4],
  },
  nodeRoot: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  kindIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 50,
    flexShrink: 0,
    paddingLeft: 4,
  },
  kindIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: '1em',
    lineHeight: 1,
  },
  kindIconSmallText: {
    fontSize: '0.6em',
  },
  kindIconRoot: {
    width: 40,
    height: 40,
    backgroundColor: theme.palette.primary.main,
    fontSize: '0.7em',
  },
  nodeContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: theme.spacing(0.5, 1),
    flex: 1,
    minWidth: 0,
  },
  nodeKind: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,
  },
  nodeName: {
    fontSize: '0.75rem',
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
    marginTop: 1,
  },
  nodeSubtitle: {
    fontSize: '0.65rem',
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.disabled,
    fontStyle: 'normal',
  },
  healthDotContainer: {
    position: 'absolute' as const,
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
  },
  healthDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  healthHealthy: {
    backgroundColor: theme.palette.success.main,
  },
  healthDegraded: {
    backgroundColor: theme.palette.error.main,
  },
  healthProgressing: {
    backgroundColor: theme.palette.info.main,
  },
  healthSuspended: {
    backgroundColor: theme.palette.warning.main,
  },
  healthUnknown: {
    backgroundColor: theme.palette.grey[400],
  },
  infoTags: {
    position: 'absolute',
    bottom: -14,
    right: 4,
    display: 'flex',
    gap: 2,
  },
  infoTag: {
    backgroundColor: alpha(theme.palette.text.disabled, 0.08),
    borderRadius: 10,
    padding: '1px 6px',
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  edgeLine: {
    position: 'absolute',
    borderTop: `1px dashed ${theme.palette.grey[400]}`,
    transformOrigin: 'center',
    zIndex: 0,
    pointerEvents: 'none',
  },
  drawer: {
    width: 1200,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    padding: theme.spacing(3),
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  drawerHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    overflow: 'hidden',
  },
  drawerTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  drawerProperty: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  drawerPropertyKey: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    minWidth: 120,
    flexShrink: 0,
  },
  drawerPropertyValue: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.body2.fontSize,
    wordBreak: 'break-word',
  },
  accordion: {
    backgroundColor: alpha(
      theme.palette.type === 'dark'
        ? theme.palette.common.white
        : theme.palette.common.black,
      0.02,
    ),
    '&:before': {
      display: 'none',
    },
    boxShadow: 'none',
    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
  },
  drawerMetadataRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap' as const,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  drawerTabs: {
    minHeight: 36,
    '& .MuiTab-root': {
      minHeight: 36,
      textTransform: 'none' as const,
      fontSize: theme.typography.body2.fontSize,
    },
  },
  drawerTabContent: {
    flex: 1,
    overflow: 'auto',
    paddingTop: theme.spacing(2),
  },
  drawerEmptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
  },
}));
